import { useCallback, useEffect, useRef } from 'react';

interface Options {
  /** Whether another page exists. No sentinel is observed when false. */
  hasNextPage: boolean | undefined;
  /** True while a page is already in flight, so scrolling cannot stack requests. */
  isFetching: boolean;
  onLoadMore: () => void;
}

/**
 * Attach the returned ref to an element at the end of a list and it loads the
 * next page as that element comes into view.
 *
 * The callback is held in a ref rather than listed as a dependency: it is
 * almost always a fresh closure per render, and depending on it would tear down
 * and rebuild the observer on every render — which fires `isIntersecting` again
 * for a sentinel already on screen and requests the same page repeatedly.
 *
 * `rootMargin` starts the fetch before the sentinel is actually visible, so the
 * rows are usually there by the time the reader arrives, and the list reads as
 * continuous rather than as a series of pauses.
 */
export function useInfiniteScrollSentinel({ hasNextPage, isFetching, onLoadMore }: Options) {
  const nodeRef = useRef<HTMLDivElement | null>(null);
  const onLoadMoreRef = useRef(onLoadMore);
  onLoadMoreRef.current = onLoadMore;

  // A callback ref, not a plain one: the sentinel mounts and unmounts as
  // `hasNextPage` flips, and an effect keyed on a ref's `.current` would not
  // re-run when the node itself is replaced.
  const setNode = useCallback((node: HTMLDivElement | null) => {
    nodeRef.current = node;
  }, []);

  useEffect(() => {
    const sentinel = nodeRef.current;
    if (!sentinel || !hasNextPage) return;
    // jsdom and older browsers have no observer; the Load more button beside
    // the sentinel is the path in that case, so do nothing rather than throw.
    if (typeof IntersectionObserver === 'undefined') return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && !isFetching) onLoadMoreRef.current();
      },
      { rootMargin: '400px' },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasNextPage, isFetching]);

  return setNode;
}
